import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './ExamScheduleList.css';

const API_URL = process.env.REACT_APP_API_URL;
const GET_PERIODS_URL = `${API_URL}/Custom/GetExamPeriods`;
const GET_TIME_SLOTS_URL = `${API_URL}/Custom/GetTimeSlots`;
const GET_MY_SEAT_SCHEDULE = `${API_URL}/Custom/GetMySeatSchedule`;

export default function MySeatScheduleComponent() {
    const [publishedPeriods, setPublishedPeriods] = useState([]);
    const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(null);
    const [timeSlots, setTimeSlots] = useState([]);
    const [distinctTimes, setDistinctTimes] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [currentWeek, setCurrentWeek] = useState(0);
    const [seats, setSeats] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // For mobile aggregated view
    const [mobileData, setMobileData] = useState([]);

    // 1) Load published periods
    useEffect(() => {
        axios.get(GET_PERIODS_URL)
            .then(({ data: periods }) => {
                const pubs = (periods || []).filter(ep => ep.isPublishedStudent);
                setPublishedPeriods(pubs);
                if (pubs.length) {
                    setSelectedPeriodIndex(0);
                }
            })
            .catch(err => console.error(err))
            .finally(() => setIsLoading(false));
    }, []);

    // 2) Desktop: when selectedPeriodIndex changes, load slots & seats
    useEffect(() => {
        if (selectedPeriodIndex == null) return;
        if (!publishedPeriods.length) return;
        if (selectedPeriodIndex < 0 || selectedPeriodIndex >= publishedPeriods.length) return;

        const periodId = publishedPeriods[selectedPeriodIndex].id;
        setIsLoading(true);
        setSeats([]);
        setTimeSlots([]);

        axios.get(`${GET_TIME_SLOTS_URL}?examPeriodId=${periodId}`)
            .then(({ data: slots }) => {
                setTimeSlots(slots);

                // distinctTimes
                const times = Array.from(new Set(
                    slots.map(ts => `${ts.startTime}-${ts.endTime}`)
                )).map(str => {
                    const [startTime, endTime] = str.split('-');
                    return { startTime, endTime };
                }).sort((a, b) => a.startTime.localeCompare(b.startTime));
                setDistinctTimes(times);

                // weeks calculation
                const dates = Array.from(new Set(
                    slots.map(ts => ts.exactDate.split('T')[0])
                )).sort();
                if (!dates.length) return [];
                const minD = new Date(dates[0]), maxD = new Date(dates[dates.length - 1]);
                const firstMon = new Date(minD);
                firstMon.setDate(minD.getDate() - ((minD.getDay() + 6) % 7));
                const lastSun = new Date(maxD);
                lastSun.setDate(maxD.getDate() + (6 - ((maxD.getDay() + 6) % 7)));
                const wks = [];
                for (let w = new Date(firstMon); w <= lastSun; w.setDate(w.getDate() + 7)) {
                    const wk = [];
                    for (let i = 0; i < 7; i++) {
                        const d = new Date(w);
                        d.setDate(w.getDate() + i);
                        wk.push(d.toISOString().split('T')[0]);
                    }
                    wks.push(wk);
                }
                setWeeks(wks);

                return axios.get(`${GET_MY_SEAT_SCHEDULE}?periodId=${periodId}`);
            })
            .then(({ data }) => {
                setSeats(data || []);
            })
            .catch(err => console.error(err))
            .finally(() => setIsLoading(false));
    }, [selectedPeriodIndex, publishedPeriods]);

    // 3) Reset currentWeek whenever weeks change
    useEffect(() => {
        if (weeks.length) setCurrentWeek(0);
    }, [weeks]);

    // 4) Mobile: aggregate all periods' slots & seats
    useEffect(() => {
        if (!publishedPeriods.length) return;

        const fetchAll = publishedPeriods.map(p =>
            axios
                .get(`${GET_TIME_SLOTS_URL}?examPeriodId=${p.id}`)
                .then(({ data: slots }) =>
                    axios
                        .get(`${GET_MY_SEAT_SCHEDULE}?periodId=${p.id}`)
                        .then(({ data: seats }) => ({ periodName: p.name, slots, seats }))
                )
        );

        Promise.all(fetchAll)
            .then(results => {
                const combined = [];
                results.forEach(({ periodName, slots, seats }) => {
                    seats.forEach(seat => {
                        const slot = slots.find(ts => ts.id === seat.slotId);
                        if (!slot) return;
                        combined.push({
                            periodName,
                            date: slot.exactDate.split('T')[0],
                            time: { startTime: slot.startTime, endTime: slot.endTime },
                            seat
                        });
                    });
                });
                setMobileData(combined);
            })
            .catch(err => console.error(err));
    }, [publishedPeriods]);

    // VisibleDays for desktop
    const visibleDays = useMemo(() => {
        if (!weeks.length) return [];
        return weeks[currentWeek].filter(day => {
            const dow = new Date(day).getDay();
            if (dow === 0 || dow === 6) {
                return timeSlots.some(ts => ts.exactDate.startsWith(day));
            }
            return true;
        });
    }, [weeks, currentWeek, timeSlots]);

    // Mobile: unique dates across all periods
    const mobileDates = useMemo(() => {
        return Array.from(new Set(mobileData.map(e => e.date))).sort();
    }, [mobileData]);

    // Render a desktop cell
    const renderCell = (date, time) => {
        const slot = timeSlots.find(
            ts =>
                ts.exactDate.startsWith(date) &&
                ts.startTime === time.startTime &&
                ts.endTime === time.endTime
        );
        if (!slot) return <div className="cell-content empty" />;
        const seat = seats.find(s => s.slotId === slot.id) || {};
        if (!seat.slotId) return <div className="cell-content empty" />;
        return (
            <div className="cell-content assigned">
                <span className="small">{seat.examName}</span>
                <span className="very-small text-muted">
                    {seat.buildingName} – {seat.classroomCode}
                </span>
                <strong className="small">
                    {seat.seatNumber === -1 ? 'Sıra No: 🔒' : `Sıra No: ${seat.seatNumber}`}</strong>

            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </div>
            </div>
        );
    }

    if (!publishedPeriods.length) {
        return (
            <div className="alert alert-info text-center" role="alert">
                Görüntülenecek sınav takvimi bulunmamaktadır.
            </div>
        );
    }

    if (selectedPeriodIndex == null) {
        return null;
    }

    return (
        <div className="container mt-4">

            <h2>Sınav Takvimi</h2>
            {/*<h3>{publishedPeriods[selectedPeriodIndex].name}</h3>*/}

            {/* Desktop-only period tabs */}
            <div className="alert alert-danger">Sıra numarası ilgili sınavdan 30 dakika önce görünür hale gelecektir.</div>
            {/*<div className="d-none d-md-block">*/}
            {/*    <ul className="nav nav-tabs mb-3">*/}
            {/*        {publishedPeriods.map((p, idx) => (*/}
            {/*            <li className="nav-item" key={p.id}>*/}
            {/*                <button*/}
            {/*                    className={`nav-link ${selectedPeriodIndex === idx ? 'active' : ''}`}*/}
            {/*                    onClick={() => {*/}
            {/*                        setSelectedPeriodIndex(idx);*/}
            {/*                        setCurrentWeek(0);*/}
            {/*                    }}*/}
            {/*                >*/}
            {/*                    {p.name}*/}
            {/*                </button>*/}
            {/*            </li>*/}
            {/*        ))}*/}
            {/*    </ul>*/}
            {/*</div>*/}

            {/* Desktop: Grid */}
            {/*<div className="d-none d-md-block">*/}
            {/*    {weeks.length > 1 && (*/}
            {/*        <div className="d-flex justify-content-between mb-2">*/}
            {/*            <button*/}
            {/*                className="btn btn-sm btn-outline-primary"*/}
            {/*                onClick={() => setCurrentWeek(w => Math.max(0, w - 1))}*/}
            {/*                disabled={currentWeek === 0}*/}
            {/*            >*/}
            {/*                &larr; Ön. Hafta*/}
            {/*            </button>*/}
            {/*            <strong>*/}
            {/*                {new Date(weeks[currentWeek][0]).toLocaleDateString('tr-TR', {*/}
            {/*                    year: 'numeric', month: 'long', day: 'numeric'*/}
            {/*                })}{' '}*/}
            {/*                –{' '}*/}
            {/*                {new Date(weeks[currentWeek][6]).toLocaleDateString('tr-TR', {*/}
            {/*                    year: 'numeric', month: 'long', day: 'numeric'*/}
            {/*                })}*/}
            {/*            </strong>*/}
            {/*            <button*/}
            {/*                className="btn btn-sm btn-outline-primary"*/}
            {/*                onClick={() => setCurrentWeek(w => Math.min(weeks.length - 1, w + 1))}*/}
            {/*                disabled={currentWeek === weeks.length - 1}*/}
            {/*            >*/}
            {/*                Son. Hafta &rarr;*/}
            {/*            </button>*/}
            {/*        </div>*/}
            {/*    )}*/}
            {/*    <div style={{*/}
            {/*        overflowX: 'auto',*/}
            {/*        display: 'grid',*/}
            {/*        gridTemplateColumns: `120px repeat(${visibleDays.length},1fr)`,*/}
            {/*        gridAutoRows: '80px',*/}
            {/*        gap: '1px',*/}
            {/*        background: '#ccc'*/}
            {/*    }}>*/}
            {/*        <div style={{ background: '#f1f1f1' }} />*/}
            {/*        {visibleDays.map(d => (*/}
            {/*            <div key={d} style={{ background: '#f1f1f1', textAlign: 'center', padding: '4px' }}>*/}
            {/*                {new Date(d).toLocaleDateString('tr-TR', {*/}
            {/*                    year: 'numeric', month: 'long', day: 'numeric'*/}
            {/*                })}*/}
            {/*            </div>*/}
            {/*        ))}*/}
            {/*        {distinctTimes.map((time, i) => (*/}
            {/*            <React.Fragment key={i}>*/}
            {/*                <div style={{*/}
            {/*                    background: '#f1f1f1',*/}
            {/*                    padding: '4px',*/}
            {/*                    border: '1px solid #bbb',*/}
            {/*                    fontSize: '0.9em',*/}
            {/*                    textAlign: 'center'*/}
            {/*                }}>*/}
            {/*                    {time.startTime.substring(0, 5)} – {time.endTime.substring(0, 5)}*/}
            {/*                </div>*/}
            {/*                {visibleDays.map(d => (*/}
            {/*                    <div key={d}>{renderCell(d, time)}</div>*/}
            {/*                ))}*/}
            {/*            </React.Fragment>*/}
            {/*        ))}*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/* Mobile: aggregate all periods into date cards */}
            <div className="d-block">
                {mobileDates.map(date => {
                    const entries = mobileData.filter(e => e.date === date);
                    return (
                        <div key={date} className="card mb-5">
                            <div style={{minHeight: "40px"}} className="card-header bg-warning text-white text-center fw-bold fs-5">
                                {new Date(date).toLocaleDateString('tr-TR', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    weekday: 'long'
                                })}
                            </div>
                            <ul className="list-group list-group-flush">
                                {entries.map((e, i) => (
                                    <li key={i} className="list-group-item">
                                        <strong>
                                            {e.time.startTime.substring(0, 5)} – {e.time.endTime.substring(0, 5)}
                                        </strong><br />
                                        {e.seat.examName}<br />
                                        <small className="text-muted">
                                            {e.seat.buildingName} – {e.seat.classroomCode}
                                        </small><br />
                                        <span className="badge bg-primary">
                                            {e.seat.seatNumber === -1 ? 'Sıra No: 🔒' : `Sıra No: ${e.seat.seatNumber}`}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
